import type { Approval, ProjectStage, Task } from '~/types/models';
import { isClientResponseComplete, isPendingClientTaskWork } from '~/lib/taskClientResponse';
import { isTaskOverdue } from '~/lib/taskWorkspace';
import { toMillis } from '~/lib/timestamps';

export type ProjectMilestoneState =
    | 'upcoming'
    | 'in_progress'
    | 'approval_required'
    | 'completed';

export type ProjectMilestoneProgress = {
    id: string;
    name: string;
    description?: string;
    order: number;
    requiresClientApproval: boolean;
    progress: number;
    completedTasks: number;
    taskCount: number;
    isCurrent: boolean;
    isComplete: boolean;
    state: ProjectMilestoneState;
};

export type ProjectMilestoneOperationalStatus = ProjectMilestoneProgress & {
    approval: Approval | null;
    statusLabel: string;
    statusDetail: string;
    taskCompletionPercent: number;
    waitingOnClientCount: number;
    waitingOnTeamCount: number;
    overdueTaskCount: number;
};

export const DEFAULT_PROJECT_MILESTONES: ProjectStage[] = [
    { id: 'design', name: 'Design', order: 1, requiresClientApproval: false },
    { id: 'development', name: 'Development', order: 2, requiresClientApproval: false },
    { id: 'signoff', name: 'Signoff', order: 3, requiresClientApproval: true },
];

export function orderProjectMilestones(stages: ProjectStage[] = []): ProjectStage[] {
    return [...stages]
        .filter(stage => Boolean(String(stage.id || '').trim() && String(stage.name || '').trim()))
        .sort((left, right) => Number(left.order || 0) - Number(right.order || 0));
}

export function resolveProjectMilestones(
    stages: ProjectStage[] | null | undefined = [],
): ProjectStage[] {
    const ordered = orderProjectMilestones(stages || []);
    const source = ordered.length > 0 ? ordered : DEFAULT_PROJECT_MILESTONES;

    return source.map((stage, index) => ({
        ...stage,
        order: index + 1,
    }));
}

export function isCompletedTask(task: Task): boolean {
    return ['done', 'completed'].includes(String(task.status || '').trim().toLowerCase());
}

export function approvalMatchesProjectMilestone(
    approval: Pick<Approval, 'stageName'>,
    stage: Pick<ProjectStage, 'id' | 'name'> | string | null | undefined,
): boolean {
    const approvalStage = String(approval.stageName || '').trim().toLowerCase();
    if (!approvalStage) return false;
    if (typeof stage === 'string') return approvalStage === stage.trim().toLowerCase();
    if (!stage) return false;
    return [stage.id, stage.name].some(value => (
        approvalStage === String(value || '').trim().toLowerCase()
    ));
}

export function hasSentProjectMilestoneApproval(
    approvals: Approval[] = [],
    stage: Pick<ProjectStage, 'id' | 'name'> | string | null | undefined,
): boolean {
    return approvals.some(approval => approvalMatchesProjectMilestone(approval, stage));
}

export function resolveProjectMilestone(
    stages: ProjectStage[] = [],
    milestoneId?: string | null,
): ProjectStage | null {
    const normalizedId = String(milestoneId || '').trim();
    if (!normalizedId) return null;
    return stages.find(stage => String(stage.id || '').trim() === normalizedId) || null;
}

export function completePassedProjectMilestones(
    stages: ProjectStage[] = [],
    currentStage?: string | null,
    nextStage?: string | null,
    completedAt: unknown = new Date(),
): ProjectStage[] {
    const ordered = orderProjectMilestones(stages);
    const currentValue = String(currentStage || '').trim().toLowerCase();
    const nextValue = String(nextStage || '').trim().toLowerCase();
    const currentIndex = ordered.findIndex(stage => (
        String(stage.id || '').trim().toLowerCase() === currentValue
        || String(stage.name || '').trim().toLowerCase() === currentValue
    ));
    const nextIndex = ordered.findIndex(stage => (
        String(stage.id || '').trim().toLowerCase() === nextValue
        || String(stage.name || '').trim().toLowerCase() === nextValue
    ));

    if (currentIndex < 0 || nextIndex <= currentIndex) return ordered;

    return ordered.map((stage, index) => (
        index >= currentIndex && index < nextIndex
            ? {
                ...stage,
                completed: true,
                completedAt: stage.completedAt || completedAt,
            }
            : stage
    ));
}

export function calculateProjectMilestoneProgress(
    stages: ProjectStage[] = [],
    tasks: Task[] = [],
    currentStage?: string | null,
    approvals: Approval[] = [],
): ProjectMilestoneProgress[] {
    const ordered = orderProjectMilestones(stages);
    const currentValue = String(currentStage || '').trim().toLowerCase();
    const currentIndex = ordered.findIndex(stage => (
        String(stage.id || '').trim().toLowerCase() === currentValue
        || String(stage.name || '').trim().toLowerCase() === currentValue
    ));

    return ordered.map((stage, index) => {
        const milestoneTasks = tasks.filter(task => (
            String(task.milestoneId || '').trim() === String(stage.id || '').trim()
        ));
        const completedTasks = milestoneTasks.filter(isCompletedTask).length;
        const derivedProgress = typeof stage.progress === 'number' && Number.isFinite(stage.progress)
            ? Math.max(0, Math.min(100, Math.round(stage.progress)))
            : null;
        const taskProgress = derivedProgress ?? (milestoneTasks.length > 0
            ? Math.round((completedTasks / milestoneTasks.length) * 100)
            : 0);
        const stageApprovals = approvals.filter(approval => approvalMatchesProjectMilestone(approval, stage));
        const approvalSatisfied = stageApprovals.some(approval => approval.status === 'approved');
        const approvalOutstanding = stageApprovals.some(approval => (
            approval.status === 'pending' || approval.status === 'rejected'
        ));
        const explicitlyComplete = stage.completed === true
            || (currentIndex >= 0 && index < currentIndex)
            || (stage.requiresClientApproval && approvalSatisfied);
        const progress = explicitlyComplete ? 100 : taskProgress;
        const approvalRequired = stage.requiresClientApproval
            && !explicitlyComplete
            && (approvalOutstanding || progress === 100);
        const state: ProjectMilestoneState = explicitlyComplete
            ? 'completed'
            : approvalRequired
                ? 'approval_required'
                : index === currentIndex || progress > 0
                    ? 'in_progress'
                    : 'upcoming';

        return {
            id: stage.id,
            name: stage.name,
            description: stage.description,
            order: stage.order,
            requiresClientApproval: stage.requiresClientApproval,
            progress,
            completedTasks,
            taskCount: milestoneTasks.length,
            isCurrent: index === currentIndex,
            isComplete: state === 'completed',
            state,
        };
    });
}

export function calculateOverallMilestoneProgress(milestones: ProjectMilestoneProgress[]): number {
    if (milestones.length === 0) return 0;
    return Math.round(
        milestones.reduce((total, milestone) => total + milestone.progress, 0) / milestones.length,
    );
}

export function calculateProjectMilestoneOperationalStatus(
    stages: ProjectStage[] = [],
    tasks: Task[] = [],
    currentStage?: string | null,
    approvals: Approval[] = [],
    now = new Date(),
): ProjectMilestoneOperationalStatus[] {
    return calculateProjectMilestoneProgress(stages, tasks, currentStage, approvals).map((milestone) => {
        const milestoneTasks = tasks.filter(task => String(task.milestoneId || '').trim() === String(milestone.id).trim());
        const taskCompletionPercent = milestoneTasks.length
            ? Math.round((milestoneTasks.filter(isCompletedTask).length / milestoneTasks.length) * 100)
            : 0;
        const waitingOnClientCount = milestoneTasks.filter(isPendingClientTaskWork).length;
        const overdueTaskCount = milestoneTasks.filter(task => isTaskOverdue(task, now)).length;
        const waitingOnTeamCount = milestoneTasks.filter((task) => {
            if (task.clientResponseRequired !== true || isClientResponseComplete(task)) return false;
            const latestMessage = Array.isArray(task.clientResponseThread) ? task.clientResponseThread.at(-1) : null;
            return latestMessage?.authorType === 'client'
                || ['completed', 'submitted'].includes(String(task.clientResponseStatus || '').trim().toLowerCase());
        }).length;
        const approval = approvals
            .filter(item => approvalMatchesProjectMilestone(item, milestone))
            .sort((left, right) => toMillis(right.respondedAt || right.createdAt) - toMillis(left.respondedAt || left.createdAt))[0] || null;

        let statusLabel = milestone.isComplete ? 'Complete' : milestone.state === 'approval_required' ? 'Approval required' : milestone.isCurrent ? 'In progress' : 'Upcoming';
        if (waitingOnClientCount) statusLabel = 'Waiting on client';
        if (waitingOnTeamCount) statusLabel = 'Client replied';
        if (approval?.status === 'pending') statusLabel = 'Awaiting approval';
        if (approval?.status === 'rejected') statusLabel = 'Changes requested';
        if (approval?.status === 'approved') statusLabel = 'Approved';
        if (overdueTaskCount && ['In progress', 'Upcoming'].includes(statusLabel)) {
            statusLabel = `${overdueTaskCount} task${overdueTaskCount === 1 ? '' : 's'} overdue`;
        }

        const details: string[] = [];
        details.push(milestone.taskCount
            ? `${milestone.progress}% complete · ${milestone.completedTasks}/${milestone.taskCount} tasks`
            : milestone.isComplete ? '100% complete · No tasks' : 'No tasks');
        if (waitingOnClientCount) details.push(`${waitingOnClientCount} question${waitingOnClientCount === 1 ? '' : 's'} waiting on the client`);
        if (waitingOnTeamCount) details.push(`${waitingOnTeamCount} client repl${waitingOnTeamCount === 1 ? 'y needs' : 'ies need'} a team response`);
        if (overdueTaskCount) details.push(`${overdueTaskCount} task${overdueTaskCount === 1 ? '' : 's'} overdue`);
        if (approval?.status === 'pending') details.push('Approval request sent to the client');
        if (approval?.status === 'approved') details.push('Client approval received');
        if (approval?.status === 'rejected') details.push('Client feedback needs review');
        if (!approval && milestone.requiresClientApproval) details.push('Client approval required before completion');

        return { ...milestone, approval, statusLabel, statusDetail: details.join(' · '), taskCompletionPercent, waitingOnClientCount, waitingOnTeamCount, overdueTaskCount };
    });
}
